import { useState, useEffect } from 'react';
import firebase from '../components/firebase';

/**
 * Two products sharing a name give Google two pages it cannot tell apart, so it
 * indexes one and drops the other as a duplicate. This warns before that happens.
 *
 * The whole name list is fetched once when the form opens (admin-only screen,
 * ~110 docs) so the comparison can be case- and whitespace-insensitive without
 * hitting Firestore on every keystroke.
 */

const normalize = str => String(str ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

/** Authoritative check, run again on save so a stale form can't slip one past. */
export async function findDuplicateName(name, excludeId = null) {
  const target = normalize(name);
  if (!target) return null;
  const snapshot = await firebase.firestore().collection('Products').get();
  const match = snapshot.docs.find(
    doc => doc.id !== excludeId && normalize(doc.data().name) === target
  );
  return match ? { id: match.id, name: match.data().name } : null;
}

export function useDuplicateName(name, excludeId = null) {
  const [products, setProducts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    firebase.firestore().collection('Products').get()
      .then(snapshot => {
        if (cancelled) return;
        setProducts(snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name || '' })));
      })
      .catch(error => {
        console.error('Could not load product names for the duplicate check:', error);
        if (!cancelled) setProducts([]);
      });
    return () => { cancelled = true; };
  }, []);

  const normalized = normalize(name);
  const match = products && normalized
    ? products.find(p => p.id !== excludeId && normalize(p.name) === normalized)
    : null;

  return { loading: products === null, match: match || null };
}
